// PRO Drift Auditor -- live actionable execution blotter.
//
// Exact BUY / SELL / HOLD order tickets to transition from the current (live or
// terminal-drifted) book to the AI regime-tilted target, in dollars and weights,
// with estimated fees and pre/post risk-contribution.

import React, { useMemo } from 'react';
import { ArrowDownRight, ArrowUpRight, ClipboardList, Minus } from 'lucide-react';

import { Card } from './Card';
import { SymbolLink } from './SymbolLink';
import { fmtMoney, fmtMoneySigned, fmtPct } from '../format';
import type { ActionableTradeOrder, TradeAction } from '../types';

const ACTION_META: Record<TradeAction, { cls: string; icon: React.ReactNode }> = {
    BUY: {
        cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
        icon: <ArrowUpRight className="h-3.5 w-3.5" />,
    },
    SELL: {
        cls: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
        icon: <ArrowDownRight className="h-3.5 w-3.5" />,
    },
    HOLD: {
        cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300',
        icon: <Minus className="h-3.5 w-3.5" />,
    },
};

export const LiveBlotterTable: React.FC<{
    rows: ActionableTradeOrder[];
    routes?: Record<string, string>;
}> = ({ rows, routes }) => {
    const totals = useMemo(() => {
        const buys = rows.filter((r) => r.action === 'BUY').reduce((s, r) => s + r.trade_dollars, 0);
        const sells = rows.filter((r) => r.action === 'SELL').reduce((s, r) => s + Math.abs(r.trade_dollars), 0);
        const fees = rows.reduce((s, r) => s + r.estimated_fee_dollars, 0);
        return { buys, sells, fees };
    }, [rows]);

    return (
        <Card
            title="Live Execution Trade Insights"
            subtitle="Actionable order tickets to move from your current book to the Forge Ultra regime-tilted target."
            icon={<ClipboardList className="h-4 w-4" />}
            right={
                <div className="hidden sm:flex items-center gap-4 text-right">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-500">Buys</div>
                        <div className="text-sm font-black tabular-nums text-emerald-600 dark:text-emerald-400">{fmtMoney(totals.buys)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-rose-500">Sells</div>
                        <div className="text-sm font-black tabular-nums text-rose-600 dark:text-rose-400">{fmtMoney(totals.sells)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Est. Fees</div>
                        <div className="text-sm font-black tabular-nums text-slate-600 dark:text-slate-300">{fmtMoney(totals.fees)}</div>
                    </div>
                </div>
            }
            bodyClassName=""
        >
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-y border-slate-100 dark:border-slate-800 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            <th className="px-3.5 py-2.5 text-left">Ticker</th>
                            <th className="px-3.5 py-2.5 text-left">Action</th>
                            <th className="px-3.5 py-2.5 text-right">Current %</th>
                            <th className="px-3.5 py-2.5 text-right">Forge Ultra Target %</th>
                            <th className="px-3.5 py-2.5 text-right">Current $</th>
                            <th className="px-3.5 py-2.5 text-right">Trade $</th>
                            <th className="px-3.5 py-2.5 text-right">Est. Fee</th>
                            <th className="px-3.5 py-2.5 text-right">Risk Δ (PCTR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const meta = ACTION_META[r.action];
                            const riskDelta = r.risk_contribution_after_pct - r.risk_contribution_before_pct;
                            return (
                                <tr
                                    key={r.ticker}
                                    className="border-b border-slate-50 dark:border-slate-800/60 last:border-0 hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                                >
                                    <td className="px-3.5 py-3 whitespace-nowrap">
                                        <SymbolLink symbol={r.ticker} routes={routes} />
                                    </td>
                                    <td className="px-3.5 py-3">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${meta.cls}`}>
                                            {meta.icon}
                                            {r.action}
                                        </span>
                                    </td>
                                    <td className="px-3.5 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">{fmtPct(r.current_weight_pct, 1)}</td>
                                    <td className="px-3.5 py-3 text-right tabular-nums font-semibold text-indigo-600 dark:text-indigo-300">{fmtPct(r.ai_regime_target_weight_pct, 1)}</td>
                                    <td className="px-3.5 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">{fmtMoney(r.current_dollars)}</td>
                                    <td className={`px-3.5 py-3 text-right tabular-nums font-bold ${r.action === 'BUY' ? 'text-emerald-600 dark:text-emerald-400' : r.action === 'SELL' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'}`}>
                                        {r.action === 'HOLD' ? '—' : fmtMoneySigned(r.trade_dollars)}
                                    </td>
                                    <td className="px-3.5 py-3 text-right tabular-nums text-slate-500 dark:text-slate-400">{r.estimated_fee_dollars > 0 ? fmtMoney(r.estimated_fee_dollars) : '—'}</td>
                                    <td className={`px-3.5 py-3 text-right tabular-nums ${riskDelta < 0 ? 'text-emerald-600 dark:text-emerald-400' : riskDelta > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'}`}>
                                        {riskDelta >= 0 ? '+' : ''}{riskDelta.toFixed(1)}%
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};
